import cx from 'classnames';
import type {
  FunctionComponent,
  PropsWithChildren,
  ReactElement,
  ReactNode,
} from 'react';
import { useEffect, useState } from 'react';

import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import {
  boardSwitcherSharedState,
  keyboardNavSharedState,
} from '@trello/board-switcher';
import { Button } from '@trello/nachos/button';
import { DownIcon } from '@trello/nachos/icons/down';
import { useSharedState } from '@trello/shared-state';
import { getTestId, type BoardSwitcherTestIds } from '@trello/test-ids';

import { focusBoardSwitcherNavItem } from './focusBoardSwitcherNavItem';
import { getBoardSwitcherSource } from './getBoardSwitcherSource';
import { useSetActiveBoardTileOnHoverOrFocus } from './useSetActiveBoardTileOnHoverOrFocus';

import * as styles from './ExpandSection.module.less';

interface ExpandSectionProps {
  id: string;
  title: ReactNode;
  customIcon?: ReactElement;
  isExpandedByDefault?: boolean;
  actions?: ReactNode;
  workspaceId?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  testId?: BoardSwitcherTestIds;
}

export const ExpandSection: FunctionComponent<
  PropsWithChildren<ExpandSectionProps>
> = ({
  id,
  title,
  customIcon,
  isExpandedByDefault = false,
  actions,
  children,
  workspaceId,
  onMouseEnter,
  onMouseLeave,
  testId = 'board-switcher-expand-section',
}) => {
  const [{ expandSectionsState }] = useSharedState(boardSwitcherSharedState);
  const [{ activeId, activeSection }] = useSharedState(keyboardNavSharedState);
  const [isExpanded, setIsExpanded] = useState<boolean>(
    expandSectionsState[id] ?? isExpandedByDefault,
  );
  const [isHovered, setIsHovered] = useState<boolean>(false);

  // Intentionally omitting onMouseMove and onMouseLeave handlers because we
  // don't want the expand button to be considered "active" when the user is
  // is using the mouse.
  const { onBlur, onFocus } = useSetActiveBoardTileOnHoverOrFocus(
    'workspace',
    id,
  );

  const isActive = activeId === id && activeSection === 'workspace';

  useEffect(() => {
    if (isActive) {
      focusBoardSwitcherNavItem('workspace', id);
    }
  }, [id, isActive]);

  useEffect(() => {
    boardSwitcherSharedState.setValue({
      expandSectionsState: {
        ...expandSectionsState,
        [id]: isExpanded,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);

  const onClick = () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'expandWorkspaceSectionButton',
      source: getBoardSwitcherSource(),
      attributes: {
        isExpanded: !isExpanded,
      },
      containers: formatContainers({
        workspaceId,
      }),
    });

    setIsExpanded(!isExpanded);
  };

  const onMouseEnterButton = () => {
    setIsHovered(true);
  };

  const onMouseLeaveButton = () => {
    setIsHovered(false);
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-testid={getTestId<BoardSwitcherTestIds>(testId)}
      className={styles.expandSection}
    >
      <div
        className={cx({ [styles.heading]: true, [styles.active]: isActive })}
      >
        <Button
          appearance="subtle"
          shouldFitContainer
          onClick={onClick}
          onMouseEnter={onMouseEnterButton}
          onMouseLeave={onMouseLeaveButton}
          className={styles.button}
          iconBefore={
            !isHovered && customIcon ? (
              customIcon
            ) : (
              <DownIcon
                dangerous_className={cx({
                  [styles.icon]: true,
                  [styles.expanded]: isExpanded,
                })}
              />
            )
          }
          onBlur={onBlur}
          onFocus={onFocus}
          data-nav-item="expand-button"
          data-section="workspace"
          data-id={id}
          aria-expanded={isExpanded}
        >
          <span className={styles.title}>{title}</span>
        </Button>
        {actions && <span className={styles.actions}>{actions}</span>}
      </div>
      {isExpanded && children}
    </div>
  );
};
